import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/*
 * 페이지를 옮길 때 스크롤 위치를 정한다.
 *
 * 1) 새 페이지는 맨 위에서 시작한다.
 *    BrowserRouter 는 스크롤을 건드리지 않아, 앞 페이지에서 내려간 만큼 새 페이지도
 *    중간에서 열렸다 — 파트너의 '연동 솔루션 보기'·'적용사례 보기', 메인 메뉴가 다 그랬다.
 *    (전에는 개인정보처리방침·이용약관만 각자 맨 위로 올리고 있었다. 여기로 모았다.)
 *
 * 2) 뒤로·앞으로 가면 보던 자리로 돌아간다.
 *    브라우저의 자동 복원은 목록을 서버에서 늦게 받아 그리는 페이지에서 엉뚱한 곳에
 *    멈췄다(파트너 맨 아래 4621 → 돌아오면 407). 되돌리는 순간 아직 내용이 없어 페이지가
 *    짧았기 때문이다. 자동 복원을 끄고, 떠날 때 위치를 적어 두었다가 내용이 그 높이까지
 *    그려질 때까지 기다려(최대 3초) 되돌린다. 그 사이 사용자가 직접 스크롤하면 멈춘다.
 *
 * 맨 위로 보내지 않는 경우
 *  - 주소에 # 이 있거나 state.scrollTo 가 있을 때: 그 페이지가 해당 위치로 옮긴다.
 *  - 그렇게 섹션으로 옮긴 뒤 state 만 지우는 주소 교체(메인): 그 자리를 지킨다.
 *  - 목록 위에 모달을 여닫을 때(/use-cases ↔ /use-cases/12, /products ↔ /products/:id):
 *    같은 화면이므로 보던 목록 위치를 지킨다.
 *
 * 지금 보고 있는 메뉴를 다시 누르면 맨 위로 올린다. React Router 는 같은 주소로 가는 링크를
 * 이동(PUSH)이 아니라 교체(REPLACE)로 처리하므로, 교체도 이동과 똑같이 다룬다.
 * (교체는 그 밖에 옛 주소 → 정본 주소, /updates → / 같은 곳에서 쓰인다 — 모두 새 화면이다.)
 */
const MODAL_SCREENS = [/^\/use-cases(\/|$)/, /^\/products(\/|$)/];
const screenOf = (path) => MODAL_SCREENS.find((re) => re.test(path))?.source ?? path;

// 새로고침해도 뒤로 가기 위치가 남도록 세션에 둔다. 못 쓰는 환경이면 메모리만 쓴다.
const STORE_KEY = 'scroll-positions';
const MAX_ENTRIES = 50;
const loadPositions = () => {
    try {
        return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
    } catch {
        return {};
    }
};
const savePositions = (positions) => {
    const keys = Object.keys(positions);
    if (keys.length > MAX_ENTRIES) keys.slice(0, keys.length - MAX_ENTRIES).forEach((k) => delete positions[k]);
    try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify(positions));
    } catch {
        // 저장하지 못해도 이번 방문 안에서는 동작한다.
    }
};

/** 내용이 y 까지 그려질 때까지 기다리며 되돌린다. 사용자가 먼저 움직이면 그만둔다. */
const restoreTo = (y) => {
    if (!y) {
        window.scrollTo(0, 0);
        return;
    }
    const USER_INPUT = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    let stopped = false;
    const stop = () => { stopped = true; };
    USER_INPUT.forEach((t) => window.addEventListener(t, stop, { passive: true }));
    const done = () => USER_INPUT.forEach((t) => window.removeEventListener(t, stop));

    const started = performance.now();
    const tick = () => {
        if (stopped) return done();
        const max = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo(0, Math.min(y, Math.max(max, 0)));
        if (max >= y || performance.now() - started > 3000) return done();
        requestAnimationFrame(tick);
        return undefined;
    };
    tick();
};

const ScrollManager = () => {
    const location = useLocation();
    const navType = useNavigationType();
    const prev = useRef({ key: location.key, path: location.pathname, sectionJump: !!location.state?.scrollTo });
    const positions = useRef(null);
    const lastY = useRef(0);

    useEffect(() => {
        positions.current = loadPositions();
        lastY.current = window.scrollY;
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
        const onScroll = () => { lastY.current = window.scrollY; };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // 그리기 전에 정한다 — 새 페이지가 잠깐 중간에서 보였다가 튀는 일이 없게.
    useLayoutEffect(() => {
        const from = prev.current;
        if (from.key === location.key) return; // 첫 화면
        prev.current = { key: location.key, path: location.pathname, sectionJump: !!location.state?.scrollTo };

        if (!positions.current) positions.current = loadPositions();
        positions.current[from.key] = lastY.current;
        savePositions(positions.current);

        if (navType === 'POP') {
            restoreTo(positions.current[location.key] ?? 0);
            return;
        }
        // PUSH 와 REPLACE 는 같이 다룬다(위 설명).
        if (location.hash || location.state?.scrollTo) return;
        if (from.sectionJump && from.path === location.pathname) return;
        if (from.path !== location.pathname && screenOf(from.path) === screenOf(location.pathname)) return;
        window.scrollTo(0, 0);
    }, [location.key, location.pathname, location.hash, location.state, navType]);

    return null;
};

export default ScrollManager;
